import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { EndpointStats } from '../../types';

interface EndpointDurationChartProps {
  stats: EndpointStats[];
  title?: string;
}

const PROVIDER_COLORS = {
  fal: '#6D9E51',
  gemini: '#BCD9A2',
};

function shortenEndpoint(endpoint: string, provider: 'fal' | 'gemini'): string {
  const prefix = provider === 'fal' ? 'fal' : 'gemini';
  const name = endpoint.replace(/^fal-ai\//, '').replace(/-preview$/, '');
  return `${prefix}: ${name}`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

export function EndpointDurationChart({ stats, title = 'Endpoint Avg Duration' }: EndpointDurationChartProps) {
  const data = stats
    .filter((s) => s.avgDuration > 0)
    .map((s) => ({
      name: shortenEndpoint(s.endpoint, s.provider),
      avgDuration: s.avgDuration,
      provider: s.provider,
      jobsCompleted: s.jobsCompleted,
    }));

  const hasData = data.length > 0;

  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-amber-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PROVIDER_COLORS.fal }} />
            <span className="text-[10px] text-surface-500">Fal</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: PROVIDER_COLORS.gemini }} />
            <span className="text-[10px] text-surface-500">Gemini</span>
          </div>
        </div>
      </div>

      {hasData ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 5, left: -10, bottom: 25 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 9, fill: '#64748b' }}
                angle={-45}
                textAnchor="end"
                interval={0}
                height={60}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#64748b' }}
                width={35}
                tickFormatter={(v: number) => formatDuration(v)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number) => [formatDuration(value), 'Avg Duration']}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const item = payload[0].payload as (typeof data)[number];
                    return `${label} (${item.jobsCompleted} completed jobs)`;
                  }
                  return String(label);
                }}
              />
              <Bar dataKey="avgDuration" radius={[4, 4, 0, 0]}>
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={PROVIDER_COLORS[entry.provider]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          No endpoint duration data available
        </div>
      )}
    </div>
  );
}
